import { useEffect, useRef, type ReactNode } from 'react';

interface Props {
  onClose: () => void;
  children: ReactNode;
}

/**
 * The settings while a game is being played: a panel over the menu rather than a page of it.
 *
 * With a game on the table the menu is three buttons — continue, settings, quit — so sending the
 * player down a settings page and back reads as leaving the game. Floating the panel keeps the
 * game and the way back to it in sight, and Escape closes the panel instead of the menu behind it.
 */
export function InGameSettings({ onClose, children }: Props) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    cardRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Without this the app's own Escape handler closes the menu and drops us into the game.
      e.stopPropagation();
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div className="confirm" role="presentation" onClick={onClose}>
      <div
        ref={cardRef}
        className="settings-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        tabIndex={-1}
        data-testid="settings-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
